import React, { useCallback, useMemo } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { AppTakeoverMenuSectionTransition } from 'features/app-takeover/components/app-takeover-menu-section-transition';
import { useAppTakeoverMenuSection } from 'features/app-takeover/hooks/use-app-takeover';
import { MenuHeaderNavigation } from 'features/menu/components/menu-header-navigation';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import MenuTileGrid from 'features/menu/components/menu-tile-grid';
import { MenuSkeletonLoader } from 'features/menu/components/menu-tile-grid/menu-tile-grid-loading';
import { MainMenuObject } from 'features/menu/components/menu-tile-grid/menu-tile/types';
import { useFetchMainMenuOnMount } from 'features/menu/hooks/use-fetch-main-menu';
import {
  IMenuObjectWithContentOptions,
  useGetVariantContentOptions,
} from 'features/menu/hooks/use-get-variant-content-options';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { useTargetItemIds } from 'features/menu/hooks/use-target-item-ids';
import { reduceMenuSectionOptions } from 'features/menu/pages/main-menu-page/utils';
import { MenuSectionTabs } from 'features/menu/pages/menu-object-page/menu-section/menu-section-tabs';
import { useFilterSectionOptionsByDayPart } from 'features/menu/pages/menu-object-page/menu-section/use-filter-section-options-by-day-part';
import { useLogMenuContent } from 'features/menu/pages/menu-object-page/use-log-menu-content';
import { pinTargetItems } from 'features/menu/utils/pin-target-items';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { FullWidthAutoHeightImage } from 'components/picture';
import { MenuDayPartUnavailable } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-day-part-unavailable';
import { UnavailableSectionRecovery } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/unavailable-section-recovery';
import { useClearUnavailableItemIdWhenAvailable } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-clear-unavailable-item-id-when-available';
import { useUnavailableItemId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/hooks/use-unavailable-item-id';
import { injectAndPinLegacyUnavailableSectionItems } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/inject-and-pin-legacy-unavailable-section-items';
import { resolveDeeplinkUnavailableItemId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/resolve-deeplink-unavailable-item-id';
import { useDeeplinksFlowExperiment } from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { getStaticPathname } from 'navigation/location/location';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useServiceModeLive } from 'state/service-mode';
import { IMenuObjectWithDaypart } from 'utils/availability';
import {
  getMenuItemDayParts,
  isAvailableForActiveDayParts,
} from 'utils/availability/daypart-availability';
import { routes } from 'utils/routing';

// OLD
export const MenuSection = () => {
  const pathname = getStaticPathname();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const isMounting = useFetchMainMenuOnMount();
  const {
    isEnabled: isDeeplinksFlowEnabled,
    isLoading: isExperimentLoading,
  } = useDeeplinksFlowExperiment();

  useMenuScroll();

  const {
    data,
    getPricingAndAvailability,
    getSection,
    isMenuLoading,
    isOffer,
    storeMenuCalled,
    storeMenuLoading,
    activeDayParts,
  } = useMainMenuContext();
  const serviceMode = useServiceModeLive();

  const filterSectionOptionsByDayPart = useFilterSectionOptionsByDayPart();
  const targetItemIds = useTargetItemIds();
  const unavailableItemIdParam = useUnavailableItemId();
  const treatmentUnavailableItemId = isDeeplinksFlowEnabled ? unavailableItemIdParam : undefined;

  const isStoreMenuReady = storeMenuCalled && !storeMenuLoading && !isMenuLoading && !isMounting;

  const isAvailableAtStore = useCallback(
    (entryId: string) => Boolean(getPricingAndAvailability(entryId)?.isAvailable),
    [getPricingAndAvailability]
  );

  const unavailableItemNowAvailable = Boolean(
    treatmentUnavailableItemId && isStoreMenuReady && isAvailableAtStore(treatmentUnavailableItemId)
  );

  useClearUnavailableItemIdWhenAvailable({
    unavailableItemId: treatmentUnavailableItemId,
    isStoreMenuReady,
    isAvailableAtStore: unavailableItemNowAvailable,
  });

  const menuSection = useMemo(() => filterSectionOptionsByDayPart(getSection(id)), [
    filterSectionOptionsByDayPart,
    getSection,
    id,
  ]);

  const menuSectionOptions = useMemo(() => {
    // Control: pin marketing targets already in the section — never inject.
    if (!treatmentUnavailableItemId) {
      return pinTargetItems((menuSection?.options ?? []) as MainMenuObject[], targetItemIds);
    }
    const pinTargets = Array.from(new Set([...targetItemIds, treatmentUnavailableItemId]));
    return pinTargetItems((menuSection?.options ?? []) as MainMenuObject[], pinTargets);
  }, [menuSection?.options, targetItemIds, treatmentUnavailableItemId]);

  const {
    inlineTileSections,
    standardSections,
    totalSectionsLength,
    deeplinkUnavailableItemId,
  } = useMemo(() => {
    const reduced = reduceMenuSectionOptions({
      menuSectionOptions,
      serviceMode,
      getPricingAndAvailability,
    });

    // Control / treatment without unavailableItemId: no injection or chrome.
    if (!treatmentUnavailableItemId) {
      return {
        ...reduced,
        deeplinkUnavailableItemId: undefined,
      };
    }

    const injectTargets = Array.from(new Set([...targetItemIds, treatmentUnavailableItemId]));

    const { items } = injectAndPinLegacyUnavailableSectionItems(
      reduced.standardSections,
      menuSectionOptions,
      injectTargets
    );

    return {
      ...reduced,
      standardSections: items,
      totalSectionsLength: Math.max(reduced.totalSectionsLength, items.length),
      deeplinkUnavailableItemId: resolveDeeplinkUnavailableItemId({
        unavailableItemIdParam: treatmentUnavailableItemId,
        isAvailableAtStore,
      }),
    };
  }, [
    getPricingAndAvailability,
    isAvailableAtStore,
    menuSectionOptions,
    serviceMode,
    targetItemIds,
    treatmentUnavailableItemId,
  ]);

  const handleUnavailableChooseRestaurant = useCallback(() => {
    if (!deeplinkUnavailableItemId) {
      return;
    }
    const item = menuSectionOptions.find(option => option._id === deeplinkUnavailableItemId);
    const typeSegment = String(item?._type ?? 'item').toLowerCase();
    // After store pick, useSelectStore opens this PDP; available → product modal,
    // unavailable → MenuProduct redirects to section chrome.
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: `${routes.menu}/${typeSegment}/${deeplinkUnavailableItemId}`,
        unavailableItemId: deeplinkUnavailableItemId,
      },
    });
  }, [deeplinkUnavailableItemId, menuSectionOptions, router]);

  useLogMenuContent({
    loggedValues: {
      id: menuSection?._id || '',
      name: menuSection?.name?.locale || '',
      menuType: menuSection?._type || '',
    },
    pathname,
  });

  const getVariantContentOptions = useGetVariantContentOptions();

  const {
    headerBackgroundColor: takeoverHeaderBg,
    headerBackgroundImage,
    headerBackgroundImageLayout,
    footerImage: takeoverFooterImage,
    footerImageOffset: takeoverFooterImageOffset,
    itemBackgroundColor: takeoverBg,
  } = useAppTakeoverMenuSection(id);

  if (isMenuLoading || isMounting) {
    return (
      <>
        <MenuHeaderNavigation title="" />
        <MenuSkeletonLoader />
      </>
    );
  }

  // Missing section or emptied by daypart / availability (e.g. breakfast vs lunch).
  if (!menuSection || totalSectionsLength <= 0) {
    if (isExperimentLoading) {
      return (
        <>
          <MenuHeaderNavigation title="" />
          <MenuSkeletonLoader />
        </>
      );
    }

    const rawSection = id ? getSection(id) : null;
    const sectionDayParts = rawSection
      ? getMenuItemDayParts(rawSection as IMenuObjectWithDaypart)
      : [];
    const isSectionOutOfDayPart = Boolean(
      rawSection &&
        sectionDayParts.length > 0 &&
        !isAvailableForActiveDayParts({
          activeDayParts,
          menuData: rawSection as IMenuObjectWithDaypart,
        })
    );

    if (isDeeplinksFlowEnabled && isSectionOutOfDayPart) {
      return <MenuDayPartUnavailable kind="category" dayParts={sectionDayParts} />;
    }

    if (isDeeplinksFlowEnabled && id) {
      return (
        <UnavailableSectionRecovery
          sectionId={id}
          targetItemIds={targetItemIds}
          unavailableItemId={treatmentUnavailableItemId}
        />
      );
    }

    return <MenuItemUnavailable isOffer={isOffer} />;
  }

  const menuSectionName =
    getVariantContentOptions(menuSection as IMenuObjectWithContentOptions).name?.locale ?? '';

  const headerBackgroundColorOverride = headerBackgroundImage
    ? 'transparent'
    : takeoverHeaderBg
    ? takeoverHeaderBg
    : undefined;
  const shouldCoverTabs = headerBackgroundImageLayout === 'cover-tabs';

  const tabs = (
    <>
      <MenuHeaderNavigation
        title={menuSectionName}
        key={menuSectionName}
        headerBackgroundColorOverride={headerBackgroundColorOverride}
      />
      <MenuSectionTabs
        menuData={data?.Menu}
        menuDataLoading={isMenuLoading}
        headerBackgroundColorOverride={headerBackgroundColorOverride}
      />
    </>
  );

  const takeoverFooterSpacer = takeoverFooterImageOffset && (
    <View style={{ width: '100%', height: takeoverFooterImageOffset }}></View>
  );

  return (
    <View style={{ flex: 1, backgroundColor: takeoverBg }}>
      <AppTakeoverMenuSectionTransition sectionId={id} />
      {headerBackgroundImage ? (
        <View>
          <FullWidthAutoHeightImage
            source={headerBackgroundImage}
            contentFit={shouldCoverTabs ? 'cover' : undefined}
            style={shouldCoverTabs ? styles.coverTabsBackground : undefined}
          />
          <View style={shouldCoverTabs ? undefined : styles.tabsOverlay}>{tabs}</View>
        </View>
      ) : (
        tabs
      )}
      <RenderStandard
        id={id}
        sections={standardSections}
        footerSpacer={takeoverFooterSpacer}
        deeplinkUnavailableItemId={deeplinkUnavailableItemId}
        onUnavailableChooseRestaurant={
          deeplinkUnavailableItemId ? handleUnavailableChooseRestaurant : undefined
        }
      />
      <RenderInlineTileGrid sections={inlineTileSections} />
      {takeoverFooterImage && (
        <View pointerEvents="none" style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
          <FullWidthAutoHeightImage source={takeoverFooterImage} />
        </View>
      )}
    </View>
  );
};

interface IMenuSectionContentTilesProps {
  sections: MainMenuObject[];
  footerSpacer?: React.ReactNode;
  deeplinkUnavailableItemId?: string;
  onUnavailableChooseRestaurant?: () => void;
}

const RenderInlineTileGrid = React.memo<IMenuSectionContentTilesProps>(({ sections }) => {
  return (
    <>
      {sections.map(section => (
        <MenuTileGrid
          key={section._id}
          // TODO: There is a type mismatch here, this need to be fixed in a future refactor
          // @ts-ignore
          data={section.options || []}
          gridTitle={section.name?.locale}
          objectFitContain={true}
        />
      ))}
    </>
  );
});

const RenderStandard = React.memo<IMenuSectionContentTilesProps & { id: string }>(
  ({ sections, id, footerSpacer, deeplinkUnavailableItemId, onUnavailableChooseRestaurant }) => {
    const { bottom } = useSafeAreaInsets();
    return sections.length > 0 ? (
      <MenuTileGrid
        key={id}
        data={sections}
        paddingBottom={bottom}
        objectFitContain
        footerSpacer={footerSpacer}
        deeplinkUnavailableItemId={deeplinkUnavailableItemId}
        onUnavailableChooseRestaurant={onUnavailableChooseRestaurant}
      />
    ) : null;
  }
);

const styles = StyleSheet.create({
  coverTabsBackground: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
    aspectRatio: undefined,
  },
  tabsOverlay: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
  },
});
